import React, { useEffect, useState } from 'react';
import { ImageResizeProps } from '../types';
import { useImageProcessor } from '../hooks/useImageProcessor';

const ImageResize: React.FC<ImageResizeProps> = ({
    image,
    width,
    height,
    maintainAspectRatio = true,
    resizeMode = 'contain',
    onProcessingComplete,
    onError,
    className,
    style,
}) => {
    const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
    const { imageBlob, imageUrl, isLoading, dimensions, resizeImage } = useImageProcessor({
        image,
        onError,
    });

    useEffect(() => {
        const processImage = async () => {
            if (imageBlob && dimensions) {
                try {
                    let targetWidth = width;
                    let targetHeight = height;

                    // Calculate dimensions if maintaining aspect ratio
                    if (maintainAspectRatio) {
                        const aspectRatio = dimensions.width / dimensions.height;

                        if (resizeMode === 'contain') {
                            // Fit within the target dimensions while maintaining aspect ratio
                            if (width / height > aspectRatio) {
                                targetWidth = Math.round(height * aspectRatio);
                            } else {
                                targetHeight = Math.round(width / aspectRatio);
                            }
                        } else if (resizeMode === 'cover') {
                            // Cover the target dimensions while maintaining aspect ratio
                            if (width / height < aspectRatio) {
                                targetWidth = Math.round(height * aspectRatio);
                            } else {
                                targetHeight = Math.round(width / aspectRatio);
                            }
                        }
                        // 'stretch' mode doesn't maintain aspect ratio, so no adjustments needed
                    }

                    const resizedBlob = await resizeImage(targetWidth, targetHeight);
                    const resizedUrl = URL.createObjectURL(resizedBlob);

                    setProcessedImageUrl(resizedUrl);

                    if (onProcessingComplete) {
                        onProcessingComplete(resizedBlob);
                    }
                } catch (err) {
                    console.error('Error resizing image:', err);
                    if (onError && err instanceof Error) {
                        onError(err);
                    }
                }
            }
        };

        processImage();

        // Cleanup function
        return () => {
            if (processedImageUrl) {
                URL.revokeObjectURL(processedImageUrl);
            }
        };
    }, [imageBlob, dimensions, width, height, maintainAspectRatio, resizeMode, onProcessingComplete, onError, resizeImage]);

    if (isLoading) {
        return <div className={className} style={style}>Loading image...</div>;
    }

    return (
        <div className={className} style={style}>
            {processedImageUrl ? (
                <img
                    src={processedImageUrl}
                    alt="Resized"
                    style={{
                        maxWidth: '100%',
                        display: 'block'
                    }}
                />
            ) : (
                imageUrl && (
                    <img
                        src={imageUrl}
                        alt="Original"
                        style={{
                            maxWidth: '100%',
                            display: 'block'
                        }}
                    />
                )
            )}
        </div>
    );
};

export default ImageResize; 